import { Config, Option } from 'effect';
import {
  defaultForStage,
  type StageDefaults,
} from '@listening/utils/stage-defaults';
import { defineConfigKey, type ConfigKey } from '@listening/env/config-key';
import { modeConfig, modeFromStage, type Mode } from '@listening/env/mode';

export {
  fallbackMode,
  orViteEnv,
  viteString,
  type Mode,
} from '@listening/env/mode';

export type ModeDefaults<A> = StageDefaults<Mode, A>;

/**
 * A deployed resource's name or address: the one definition both the runtime
 * and `packages/infra` read. The runtime resolves it from its env var, else
 * from the stage whose world it is in: `STAGE` when one is set (`secretspec
 * run --profile <p>` exports it, so a `localstack`-mode child still names that
 * stage's buckets), else `MODE`, which is all a pod carries. Infra declares
 * the resource from `forMode`, so the two cannot name different things.
 * `localstack` carries `''`, the "no such resource" value the S3 and SES
 * clients already fail on loudly at the call site.
 */
export type ByMode = Readonly<Record<Mode, string>>;

export interface DeployedKey<Name extends string = string> extends ConfigKey<
  string,
  string,
  Name
> {
  readonly byMode: ByMode;
  readonly forMode: (mode: Mode) => string;
}

const stageWorldMode = Config.all([
  Config.option(Config.String('STAGE')),
  modeConfig,
]).pipe(
  Config.map(([stage, mode]) =>
    Option.match(
      Option.filter(stage, (value) => value !== ''),
      { onNone: () => mode, onSome: modeFromStage },
    ),
  ),
);

export const deployed = <const Name extends string>(
  envVarName: Name,
  byMode: ByMode,
): DeployedKey<Name> =>
  Object.assign(
    defineConfigKey(envVarName, (name) =>
      Config.all([stageWorldMode, Config.option(Config.String(name))]).pipe(
        Config.map(([mode, value]) =>
          Option.getOrElse(value, () => byMode[mode]),
        ),
      ),
    ),
    { byMode, forMode: (mode: Mode) => byMode[mode] },
  );

/** A name that follows one pattern on every deployed stage. */
export const perMode = (
  name: (mode: Exclude<Mode, 'localstack'>) => string,
): ByMode => ({
  localstack: '',
  development: name('development'),
  staging: name('staging'),
  production: name('production'),
});

/**
 * Apply mode-specific defaults to a config key.
 * Env vars always win. If not set, uses mode-appropriate default.
 *
 * @example
 * // Only production differs
 * withModeDefault(
 *   Config.schema(Schema.Literals(['local', 'remote']), 'CONVERSION_MODE'),
 *   { default: 'local', production: 'remote' }
 * )
 *
 * @example
 * // Localstack differs from deployed environments
 * withModeDefault(
 *   Config.Boolean('DEV_TOOLS'),
 *   { default: false, localstack: true }
 * )
 */
export const withModeDefault = <A>(
  key: Config.Config<A>,
  defaults: ModeDefaults<A>,
): Config.Config<A> =>
  Config.all([modeConfig, Config.option(key)]).pipe(
    Config.map(([mode, opt]) =>
      Option.getOrElse(opt, () => defaultForStage<Mode, A>(defaults, mode)),
    ),
  );

/**
 * Env var wins. If not set, uses a lazily computed fallback.
 *
 * Note: you cannot chain two `Config.withDefault` calls — the inner default always
 * wins and makes the outer unreachable. This helper uses `Config.option` to bypass
 * that problem.
 *
 * @example
 * const isLambda = yield* withLazyDefault(
 *   Config.Boolean('IS_LAMBDA'),
 *   () => Option.isSome(partialConfig.awsLambdaFunctionName),
 * );
 */
export const withLazyDefault = <A>(
  key: Config.Config<A>,
  fallback: () => A,
): Config.Config<A> =>
  Config.option(key).pipe(Config.map((opt) => Option.getOrElse(opt, fallback)));

/**
 * Pipeable combinator: env var wins, otherwise compute a default from resolved Config dependencies.
 *
 * @example
 * const trustedOrigins = Config.array(Config.String(), 'CORS_ORIGIN').pipe(
 *   withComputedDefault({ siteUrl, mode }, ({ siteUrl, mode }) =>
 *     mode === 'localstack'
 *       ? [`http://127.0.0.1:3007`, `http://127.0.0.1:3008`]
 *       : [siteUrl],
 *   ),
 * );
 */
export const withComputedDefault =
  // oxlint-disable-next-line typescript-eslint/no-explicit-any -- Config.Config variance requires any for generic constraint
  <A, Deps extends Record<string, Config.Config<any>>>(
    deps: Deps,
    fallback: (resolved: {
      [K in keyof Deps]: Config.Success<Deps[K]>;
    }) => A,
  ) =>
    (key: Config.Config<A>): Config.Config<A> =>
      Config.all([Config.option(key), Config.all(deps)]).pipe(
        Config.map(([opt, resolvedDeps]) =>
          // oxlint-disable-next-line typescript-eslint/no-unsafe-argument, typescript-eslint/no-explicit-any -- Config.all structural type needs cast to mapped type
          Option.getOrElse(opt, () => fallback(resolvedDeps as any)),
        ),
      );
